/**
 * Lightweight, dependency-free chart primitives for the reporting pages.
 * Rendered with plain CSS/SVG using the brand color tokens — no chart library.
 * Dark-first and accessible (each bar is labelled; the chart has a role).
 */

export interface BarDatum {
  key: string
  label: string
  count: number
}

/** Horizontal labelled bars — good for category breakdowns. */
export function HorizontalBars({
  data,
  emptyLabel = 'No data',
}: {
  data: BarDatum[]
  emptyLabel?: string
}) {
  const max = Math.max(1, ...data.map((d) => d.count))
  if (data.length === 0) {
    return <p className="py-8 text-center text-sm text-muted">{emptyLabel}</p>
  }
  return (
    <ul className="space-y-3" aria-label="Bar chart">
      {data.map((d) => (
        <li key={d.key} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted">{d.label}</span>
            <span className="font-medium tabular-nums text-fg">{d.count}</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-card">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${(d.count / max) * 100}%` }}
              role="img"
              aria-label={`${d.label}: ${d.count}`}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Vertical column chart for time-series buckets (months/days). */
export function ColumnChart({
  data,
  emptyLabel = 'No data',
}: {
  data: BarDatum[]
  emptyLabel?: string
}) {
  const max = Math.max(1, ...data.map((d) => d.count))
  if (data.length === 0) {
    return <p className="py-8 text-center text-sm text-muted">{emptyLabel}</p>
  }
  return (
    <div className="flex h-40 items-end gap-1.5" role="img" aria-label="Column chart">
      {data.map((d) => (
        <div key={d.key} className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <div className="flex w-full flex-1 items-end">
            <div
              className="w-full rounded-t bg-gradient-to-t from-primary/60 to-secondary transition-[height]"
              style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
              title={`${d.label}: ${d.count}`}
            />
          </div>
          <span className="w-full truncate text-center text-[10px] text-dim" title={d.label}>
            {d.label}
          </span>
          <span className="text-[10px] font-medium tabular-nums text-muted">{d.count}</span>
        </div>
      ))}
    </div>
  )
}
